"use client";

import { useRef, type ComponentType, type ReactNode, type SVGProps } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { blurReveal, reduceMotion } from "@/lib/motion";
import { TextReveal } from "@/components/motion/TextReveal";
import { cn } from "@/lib/utils";

type StoryPanelProps = {
  id: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  kicker?: string;
  children: ReactNode;
  className?: string;
};

/**
 * Large grey product panel in the feature story. Enters from below with a slight scale,
 * the icon pops, the title de-blurs, then every `[data-story-item]` settles in order.
 */
export function StoryPanel({ id, icon: Icon, title, kicker, children, className }: StoryPanelProps) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || reduceMotion()) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: "top 80%", toggleActions: PLAY_ONCE } });
      tl.fromTo(el, { opacity: 0, y: 90, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 1.35, ease: "silk" })
        .fromTo(
          el.querySelector("[data-story-icon]"),
          { opacity: 0, scale: 0.6 },
          { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.7)" },
          0.3,
        )
        .add(blurReveal(gsap.utils.toArray("[data-story-title] [data-tr-unit]", el), { stagger: 0.08 }), 0.34)
        .fromTo(
          gsap.utils.toArray("[data-story-item]", el),
          { opacity: 0, y: 22 },
          { opacity: 1, y: 0, duration: 0.95, ease: "silk", stagger: 0.09 },
          0.55,
        );

      // Only the Projects and Finance panels draw bars; an empty selection makes GSAP warn.
      const bars = gsap.utils.toArray<HTMLElement>("[data-story-bar]", el);
      if (bars.length) tl.fromTo(bars, { scaleX: 0 }, { scaleX: 1, duration: 1.3, ease: "silk", stagger: 0.08 }, 0.85);
    },
    { scope: ref },
  );

  return (
    <article
      id={id}
      ref={ref}
      data-story-panel=""
      data-reveal-init=""
      className={cn("scroll-mt-28 rounded-[28px] bg-panel p-4 sm:p-6 md:p-8", className)}
    >
      <header className="flex items-start gap-3">
        <span
          data-story-icon=""
          className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-[11px] bg-white text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
        >
          <Icon className="size-4" />
        </span>
        <div data-story-title="">
          {kicker && (
            <p data-story-item="" className="mb-1.5 text-[11.5px] font-medium uppercase tracking-[0.1em] text-accent">
              {kicker}
            </p>
          )}
          <TextReveal
            as="h3"
            mode="manual"
            text={title}
            className="text-[clamp(1.45rem,2.05vw,1.95rem)] font-[450] leading-[1.15] tracking-[-0.035em]"
          />
        </div>
      </header>
      <div className="mt-6 md:mt-8">{children}</div>
    </article>
  );
}
